import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  MenuItem,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  Typography,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { fieldStyles } from "services/utils";
import { useAuth } from "context/authContext";
import { getDepartmentById, getJobToBranch, getDepartmentDetailsByJobId } from "services/api";
import { hiringReasonOptions } from "services/utils";

const AddTalentRequisitionDialog = ({
  open,
  onClose,
  request,
  branches,
  departments,
  jobs,
  handleSave,
  handleDelete,
}) => {
  const { t } = useTranslation();

  const { employee, allEmps, setAllEmps } = useAuth();

  const [form, setForm] = useState({
    jobId: "",
    departmentId: "",
    branchId: "",
    numberOfPositions: "",
    employmentType: "",
    requiredSkills: "",
    reasonForHiring: "",
    description: "",
    isForCompany: true,
    jobTitle: "",
    departmentName: "",
    branchName: "",
  });
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [customHiringReason, setCustomHiringReason] = useState("");
  // const [departmentName, setDepartmentName] = useState("");
  // const [departmentDetails, setDepartmentDetails] = useState([]);
  const [departmentDetails, setDepartmentDetails] = useState({
    jobId: null,
    departmentId: null,
    departmentName: "",
  });

  const handleClose = () => {
    setForm({
      jobId: "",
      departmentId: "",
      branchId: "",
      numberOfPositions: "",
      employmentType: "",
      requiredSkills: "",
      reasonForHiring: "",
      description: "",
      isForCompany: true,
      jobTitle: "",
      departmentName: "",
      branchName: "",
    });
    setCustomHiringReason("");
    setErrors({});
    setLoading(false);
    onClose();
  };

  const getFilteredJobsForUser = () => {
    if (!jobs || !employee) return [];

    // Admin or role 1 → see all jobs
    if (employee.isAdmin || employee.role === 1) return jobs;

    // Normal employee with no job → no jobs
    if (!employee.jobId) return [];

    // Build a map of parent jobId → children jobs
    const childrenMap = new Map();
    jobs.forEach((job) => {
      if (job.reportingToSolidId) {
        if (!childrenMap.has(job.reportingToSolidId)) {
          childrenMap.set(job.reportingToSolidId, []);
        }
        childrenMap.get(job.reportingToSolidId).push(job);
      }
    });

    // Recursively collect all subordinate jobIds
    const collectHierarchy = (jobId, result = new Set()) => {
      const children = childrenMap.get(jobId) || [];
      for (const child of children) {
        if (!result.has(child.jobId)) {
          result.add(child.jobId);
          collectHierarchy(child.jobId, result);
        }
      }
      return result;
    };

    const hierarchyIds = collectHierarchy(employee.jobId);
    hierarchyIds.add(employee.jobId); // ✅ Include the employee's own job

    // Filter jobs by hierarchy and department
    const filteredJobs = jobs.filter(
      (j) => hierarchyIds.has(j.jobId) && j?.departmentId === departmentDetails?.departmentId
    );

    return filteredJobs;
  };

  const handleChange = (field, value) => {
    let updatedForm = { ...form, [field]: value };

    let availableJobs = getFilteredJobsForUser();

    if (field === "departmentId") {
      let deptJobs;

      if (value) {
        deptJobs = availableJobs.filter((j) => j?.departmentId === value);
      } else {
        deptJobs = availableJobs;
      }
      setFilteredJobs(deptJobs);

      if (!deptJobs.some((j) => j.jobId === updatedForm.jobId)) {
        updatedForm.jobId = "";
      }
    }

    if (field === "jobId") {
      const selectedJob = availableJobs.find((j) => j.jobId === value);
      if (selectedJob) {
        updatedForm.departmentId = selectedJob?.departmentId;

        setFilteredJobs(availableJobs.filter((j) => j?.departmentId === selectedJob?.departmentId));
      }
    }

    setForm(updatedForm);
    setErrors((prev) => ({ ...prev, [field]: false }));
  };

  useEffect(() => {
    // let availableJobs = jobs || [];
    let availableJobs = getFilteredJobsForUser();

    if (request) {
      setForm({
        jobId: request.jobId || "",
        departmentId: request.departmentId || "",
        branchId: request.branchId || "",
        numberOfPositions: request.numberOfPositions || "",
        employmentType: request.employmentType || "",
        requiredSkills: request.requiredSkills || "",
        reasonForHiring: request.reasonForHiring || "",
        description: request.description || "",
        isForCompany: request.isForCompany || true,
        jobTitle: request.jobTitle || "",
        departmentName: request?.departmentName || "",
        branchName: request.branchName || "",
      });

      // Initial filtered jobs
      // setFilteredJobs(jobs?.filter((j) => j.departmentId === request.departmentId));
      // setFilteredJobs(availableJobs?.filter(j => j.departmentId === request.departmentId));
      const deptJobs = request?.departmentId
        ? availableJobs.filter((j) => j?.departmentId === request?.departmentId)
        : availableJobs;

      setFilteredJobs(deptJobs);
    } else {
      setForm({
        jobId: "",
        departmentId: "",
        branchId: "",
        numberOfPositions: "",
        employmentType: "",
        requiredSkills: "",
        reasonForHiring: "",
        description: "",
        isForCompany: true,
        jobTitle: "",
        departmentName: "",
        branchName: "",
      });

      // setFilteredJobs(jobs);
      setFilteredJobs(availableJobs);
    }

    setErrors({});
  }, [request, jobs, departmentDetails]);

  //Fetch Department Deatils
  useEffect(() => {
    if (!employee?.jobId) return;

    const fetchDept = async () => {
      const dept = await getDepartmentDetailsByJobId(employee?.jobId);
      if (dept != "error") {
        const [jobId, departmentId, departmentName] = dept[0];
        setDepartmentDetails({ jobId, departmentId, departmentName });
      } else {
        setDepartmentDetails({ jobId: null, departmentId: null, departmentName: "" });
      }
    };

    fetchDept();
  }, [employee?.jobId]);

  const validate = () => {
    let newErrors = {};

    // 1. Common Fields (Required in both modes)
    if (!form.numberOfPositions) newErrors.numberOfPositions = true;

    // 2. Conditional Fields
    if (form.isForCompany) {
      // Mode A: Internal Company (Validate IDs)
      ["jobId", "departmentId", "branchId"].forEach((field) => {
        if (!form[field]) newErrors[field] = true;
      });
    } else {
      // Mode B: External / Free Text (Validate Strings)
      ["jobTitle", "departmentName", "branchName"].forEach((field) => {
        // Check if value exists and is not just whitespace
        if (!form[field] || form[field].trim() === "") {
          newErrors[field] = true;
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    await handleSave(form, request?.id);
    setLoading(false);
    setForm({
      jobId: "",
      departmentId: "",
      branchId: "",
      numberOfPositions: "",
      employmentType: "",
      requiredSkills: "",
      reasonForHiring: "",
      description: "",
      isForCompany: true,
      jobTitle: "",
      departmentName: "",
      branchName: "",
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ fontWeight: "bold", textAlign: "center", fontSize: { xs: "1.2rem", sm: "1.6rem" } }}
      >
        {request?.id ? t("admin.talent.editRequest") : t("admin.talent.newTalentAcquisition")}
      </DialogTitle>

      <DialogContent
        sx={{
          padding: { xs: "16px", sm: "20px 24px" },
          display: "grid",
          gap: 2,
        }}
      >
        {/* 1. Toggle Switch */}
        <Box display="flex" alignItems="center" mb={1}>
          <FormControlLabel
            control={
              <Switch
                checked={form.isForCompany}
                onChange={(e) => handleChange("isForCompany", e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="button" fontWeight="bold" color="text.secondary">
                {form.isForCompany
                  ? t("admin.talent.internalRequisition")
                  : t("admin.talent.externalRequisition")}
              </Typography>
            }
          />
        </Box>

        {/* 2. Department Logic */}
        {form.isForCompany ? (
          // CASE A: Internal (Use IDs & Autocomplete)
          employee.isAdmin || employee.role === 1 ? (
            <Autocomplete
              options={departments || []}
              getOptionLabel={(d) => d?.departmentName || ""}
              value={departments?.find((d) => d.departmentId === form?.departmentId) || null}
              onChange={(_, value) => handleChange("departmentId", value?.departmentId || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("admin.talent.department") + " *"}
                  error={!!errors.departmentId} // converted to boolean
                  helperText={errors.departmentId}
                  fullWidth
                />
              )}
            />
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", mt: 1.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mr: 1 }}>
                {t("admin.talent.department")}:
              </Typography>
              <Typography variant="body1">{departmentDetails.departmentName || "--"}</Typography>
            </Box>
          )
        ) : (
          // CASE B: External (Use Strings & TextField)
          <TextField
            label={t("admin.talent.department") + " *"}
            value={form.departmentName || ""}
            onChange={(e) => handleChange("departmentName", e.target.value)}
            error={!!errors.departmentName}
            helperText={errors.departmentName}
            fullWidth
          />
        )}

        {/* 3. Job Logic */}
        {form.isForCompany ? (
          <Autocomplete
            options={filteredJobs || []}
            getOptionLabel={(j) => j.jobName || ""}
            value={filteredJobs?.find((j) => j.jobId === form.jobId) || null}
            onChange={(_, value) => handleChange("jobId", value?.jobId || "")}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("admin.talent.job") + " *"}
                error={!!errors.jobId}
                helperText={errors.jobId}
                fullWidth
              />
            )}
          />
        ) : (
          <TextField
            label={t("admin.talent.job") + " *"}
            value={form.jobTitle || ""}
            onChange={(e) => handleChange("jobTitle", e.target.value)}
            error={!!errors.jobTitle}
            helperText={errors.jobTitle}
            fullWidth
          />
        )}

        {/* 4. Branch Logic */}
        {form.isForCompany ? (
          <Autocomplete
            options={branches || []}
            getOptionLabel={(b) => b.branchName || ""}
            value={branches?.find((b) => b.branchId === form.branchId) || null}
            onChange={(_, value) => handleChange("branchId", value?.branchId || "")}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("admin.talent.branch") + " *"}
                error={!!errors.branchId}
                helperText={errors.branchId}
                fullWidth
              />
            )}
          />
        ) : (
          <TextField
            label={t("admin.talent.branch") + " *"}
            value={form.branchName || ""}
            onChange={(e) => handleChange("branchName", e.target.value)}
            error={!!errors.branchName}
            helperText={errors.branchName}
            fullWidth
          />
        )}

        {/* Number of Positions */}
        <TextField
          type="number"
          label={t("admin.talent.numberOfPositions") + " *"}
          value={form.numberOfPositions}
          error={errors.numberOfPositions}
          // onChange={(e) => handleChange("numberOfPositions", e.target.value)}
          onChange={(e) => {
            const val = parseInt(e.target.value, 10);
            // Only update if the value is empty (allowing deletion) or greater than 0
            if (e.target.value === "" || val >= 1) {
              handleChange("numberOfPositions", e.target.value);
            }
          }}
          fullWidth
          inputProps={{ min: 1 }}
        />

        {/* Employment Type */}
        <FormControl fullWidth>
          <InputLabel>{t("admin.talent.employmentType")}</InputLabel>
          <Select
            value={form.employmentType || ""}
            label={t("admin.talent.employmentType")}
            onChange={(e) => handleChange("employmentType", e.target.value)}
            sx={fieldStyles}
          >
            <MenuItem value="FULL_TIME">{t("admin.talent.fullTime")}</MenuItem>
            <MenuItem value="PART_TIME">{t("admin.talent.partTime")}</MenuItem>
          </Select>
        </FormControl>

        {/* Required Skills */}
        <TextField
          label={t("admin.talent.requiredSkills")}
          value={form.requiredSkills}
          onChange={(e) => handleChange("requiredSkills", e.target.value)}
          fullWidth
          multiline
          minRows={2}
        />

        {/* Reason for Hiring */}
        <Autocomplete
          options={hiringReasonOptions}
          value={
            hiringReasonOptions.includes(form.reasonForHiring) ? form.reasonForHiring : "Other"
          }
          onChange={(event, newValue) => {
            if (newValue === "Other") {
              handleChange("reasonForHiring", customHiringReason);
            } else {
              handleChange("reasonForHiring", newValue || "");
            }
          }}
          renderInput={(params) => (
            <TextField {...params} label={t("admin.talent.reasonForHiring")} fullWidth />
          )}
        />

        {(form.reasonForHiring === "" ||
          !hiringReasonOptions.includes(form.reasonForHiring) ||
          form.reasonForHiring === customHiringReason) && (
          <TextField
            label={t("admin.talent.specifyReason")}
            value={customHiringReason}
            onChange={(e) => {
              setCustomHiringReason(e.target.value);
              handleChange("reasonForHiring", e.target.value);
            }}
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 2 }}
          />
        )}

        {/* Description */}
        <TextField
          label={t("admin.talent.description")}
          value={form.description}
          onChange={(e) => handleChange("description", e.target.value)}
          fullWidth
          multiline
          minRows={3}
        />
      </DialogContent>

      {/* Loading Spinner */}
      {loading ? (
        <Box display="flex" justifyContent="flex-end" p={2}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <DialogActions
          sx={{
            justifyContent: { xs: "center", sm: "space-between" },
            flexDirection: { xs: "column", sm: "row" },
            padding: "16px 24px",
            gap: 1,
          }}
        >
          <Button onClick={handleClose} color="secondary">
            {t("common.cancel")}
          </Button>

          {request?.id && (
            <Button
              color="error"
              onClick={async () => {
                setLoading(true);
                await handleDelete(request.id);
                setLoading(false);
              }}
            >
              {t("common.delete")}
            </Button>
          )}

          <Button onClick={onSubmit} color="primary">
            {request?.id ? t("common.update") : t("common.save")}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

AddTalentRequisitionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  request: PropTypes.object,
  branches: PropTypes.array.isRequired,
  departments: PropTypes.array.isRequired,
  jobs: PropTypes.array.isRequired,
  handleSave: PropTypes.func.isRequired,
  handleDelete: PropTypes.func,
};

export default AddTalentRequisitionDialog;
